/** @jsx h */
import { component, h } from 'literaljs';
import { playPop, playChime } from '../../utils/sounds';

export default component({
	name: 'MethodCard',
	props: ['method', 'onCopy', 'index'],
	state: {
		expanded: false
	},
	methods() {
		return {
			toggleExpand() {
				playPop();
				this.setState({ expanded: !this.getState().expanded });
			},
			copyCode(code) {
				playChime();
				if (this.props.onCopy) {
					this.props.onCopy(code);
				}
			}
		};
	},
	render() {
		const { expanded } = this.getState();
		const { method, index } = this.props;

		const cardClasses = ['card', 'method-card'];
		if (!expanded) cardClasses.push('card-interactive');
		if (expanded) cardClasses.push('-expanded');

		return (
			<div class={cardClasses.join(' ')}>
				<div 
					class="method-header" 
					id={`method-${method.id}`}
					events={{ click: () => this.toggleExpand() }}
				>
					<div class="method-title-group">
						<span class="method-icon">{method.icon || method.name.charAt(0).toUpperCase()}</span>
						<div class="method-title-info">
							<h2 class="method-name">{method.name}</h2>
							<span class={"method-category -" + method.category}>{method.category}</span>
						</div>
					</div>
					<div class="method-actions">
						<button
							class="method-copy-btn"
							aria-label="Copy syntax"
							events={{
								click: (e) => {
									e.stopPropagation();
									this.copyCode(method.syntax);
								}
							}}
							title="Copy syntax"
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
								<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
							</svg>
						</button>
						<button
							class="method-expand-btn"
							aria-label="Expand method details"
							aria-expanded={expanded}
							events={{
								click: (e) => {
									e.stopPropagation();
									this.toggleExpand();
								}
							}}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class={expanded ? "-rotated" : ""}>
								<polyline points="6 9 12 15 18 9"></polyline>
							</svg>
						</button>
					</div>
				</div>

				<div class={"method-body" + (expanded ? " -visible" : "")}>
					<p class="method-description">{method.description}</p>

					<div class="syntax-block">
						<div class="syntax-header">
							<span>Syntax</span>
							<button
								class="copy-btn"
								events={{ click: () => this.copyCode(method.syntax) }}
							>
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
									<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
									<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
								</svg>
								Copy
							</button>
						</div>
						<pre><code>{method.syntax}</code></pre>
					</div>

					{method.parameters && (
						<div class="params-table">
							<h3>Parameters</h3>
							{method.parameters.map(param => (
								<div class="param-row" key={param.name}>
									<span class="param-name">{param.name}</span>
									<span class="param-type">{param.type}</span>
									<span class="param-desc">{param.description}</span>
								</div>
							))}
						</div>
					)}

					{method.returns && (
						<div class="returns-block">
							<h3>Returns</h3>
							<span class="return-type">{method.returns.type}</span>
							<span class="return-desc">{method.returns.description}</span>
						</div>
					)}

					{method.methods && (
						<div class="methods-list">
							<h3>Methods</h3>
							{method.methods.map(m => (
								<div class="method-item" key={m.name}>
									<code>{m.name}</code>
									<span>{m.description}</span>
								</div>
							))}
						</div>
					)}

					<div class="example-block">
						<div class="example-header">
							<span>Example</span>
							<button
								class="copy-btn"
								events={{ click: () => this.copyCode(method.example) }}
							>
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
									<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
									<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
								</svg>
								Copy
							</button>
						</div>
						<pre><code>{method.example}</code></pre>
					</div>

					{method.relatedMethods && method.relatedMethods.length > 0 && (
						<div class="related-methods">
							<h3>Related Methods</h3>
							<div class="related-tags">
								{method.relatedMethods.map(related => (
									<span key={related} class="related-tag">{related}</span>
								))}
							</div>
						</div>
					)}

					{method.commonPatterns && method.commonPatterns.length > 0 && (
						<div class="common-patterns">
							<h3>Common Patterns</h3>
							{method.commonPatterns.map((pattern, i) => (
								<div key={i} class="pattern-card">
									<div class="pattern-name">{pattern.name}</div>
									<pre><code>{pattern.snippet}</code></pre>
								</div>
							))}
						</div>
					)}

					<div class="usage-tips">
						<h3>Usage Tips</h3>
						<ul>
							<li>Call this method from within component methods or lifecycle hooks</li>
							<li>Always use `this` to access these methods</li>
							<li>Check the parameters above for detailed usage</li>
						</ul>
					</div>
				</div>
			</div>
		);
	}
});